import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 0 });

// Count of declared amounts above their LHDN cap (or negative). Used by parents to block Save.
export function countReliefErrors(value, categories) {
  return (categories || []).filter((c) => {
    const amt = Number(value?.[c.code] || 0);
    return amt < 0 || (c.cap != null && amt > c.cap);
  }).length;
}

// Resolve the label for the employee's family situation (single staff never see spouse/child wording).
function reliefLabel(c, married, hasChildren) {
  if (!c.label_template) return c.label;
  const parts = ['self', married && 'spouse', hasChildren && 'child'].filter(Boolean);
  return c.label_template.replace('{family}', parts.join(', ')).replace('{family_slash}', parts.join(' / '));
}

// LHDN TP1 relief declarations {code: annual RM}. Over-cap amounts are flagged inline and rejected server-side.
// Child-scoped reliefs are hidden (and cleared) unless the employee has children on record.
export function TaxReliefForm({ value, onChange, onErrorsChange, maritalStatus = 'single', childrenCount = 0 }) {
  const [allCategories, setAllCategories] = useState([]);
  const v = value || {};
  const married = maritalStatus === 'married';
  const hasChildren = Number(childrenCount) > 0;

  useEffect(() => {
    axios.get(`${API}/statutory/relief-categories`).then((r) => setAllCategories(r.data || [])).catch(() => setAllCategories([]));
  }, []);

  const categories = useMemo(() => allCategories.filter((c) => c.scope !== 'child' || hasChildren), [allCategories, hasChildren]);
  const hiddenCount = allCategories.length - categories.length;

  // Drop values for reliefs that no longer apply (e.g. children set back to 0) so payroll never uses them.
  useEffect(() => {
    const stale = allCategories.filter((c) => c.scope === 'child' && !hasChildren && v[c.code] != null);
    if (stale.length) {
      const next = { ...v };
      stale.forEach((c) => delete next[c.code]);
      onChange(next);
    }
  }, [hasChildren, allCategories]);

  const { declaredTotal, errorCount } = useMemo(() => {
    let total = 0;
    categories.forEach((c) => { if (!c.monthly) total += Number(v[c.code] || 0); });
    return { declaredTotal: total, errorCount: countReliefErrors(v, categories) };
  }, [categories, v]);

  useEffect(() => { onErrorsChange?.(errorCount); }, [errorCount, onErrorsChange]);

  return (
    <div className="space-y-3" data-testid="tax-relief-form">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Annual amounts declared by the employee on Form TP1. Individual (RM 9,000){married ? ', spouse' : ''}{hasChildren ? ', children' : ''}, disabled and EPF reliefs are applied automatically from the profile.
          {hiddenCount > 0 && <span className="block mt-0.5 text-slate-400" data-testid="tax-relief-hidden-note">{hiddenCount} child-related relief{hiddenCount === 1 ? '' : 's'} hidden — set the number of children in Tax Relief Information to declare them.</span>}
        </p>
        <span className={`text-xs font-medium whitespace-nowrap ${errorCount ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`} data-testid="tax-relief-total">
          Declared: RM {fmt(declaredTotal)}
        </span>
      </div>
      {errorCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-xs text-rose-700 dark:text-rose-300" data-testid="tax-relief-over-limit" role="alert">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{errorCount} relief amount{errorCount === 1 ? '' : 's'} exceed{errorCount === 1 ? 's' : ''} the LHDN annual limit. Reduce the highlighted field{errorCount === 1 ? '' : 's'} to the maximum shown before saving.</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {categories.map((c) => {
          const amt = Number(v[c.code] || 0);
          const over = c.cap != null && amt > c.cap;
          const negative = amt < 0;
          const invalid = over || negative;
          return (
            <div key={c.code} className="grid grid-cols-12 gap-2 items-start">
              <Label className="col-span-8 text-xs font-normal leading-snug text-slate-700 dark:text-slate-300 pt-1.5">
                {reliefLabel(c, married, hasChildren)}
                <span className="block text-[10px] text-slate-400">{c.monthly ? 'RM / month' : c.cap != null ? `Max RM ${fmt(c.cap)} / year` : 'No cap'}</span>
              </Label>
              <div className="col-span-4">
                <Input type="number" min="0" step="1" value={v[c.code] ?? ''} placeholder="0"
                  onChange={(e) => onChange({ ...v, [c.code]: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                  data-testid={`relief-${c.code}`}
                  aria-invalid={invalid || undefined}
                  className={`h-8 text-right text-sm dark:bg-slate-900 dark:border-slate-600 ${invalid ? 'border-rose-500 ring-2 ring-rose-500/70 text-rose-700 dark:text-rose-300 focus-visible:ring-rose-500' : ''}`} />
                {invalid && (
                  <p className="mt-1 text-[10px] leading-tight text-rose-600 dark:text-rose-400 text-right" data-testid={`relief-${c.code}-error`}>
                    {negative ? 'Cannot be negative' : `Exceeds limit — max RM ${fmt(c.cap)}`}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
